import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Target } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

const STATUS_STYLE = {
  idea: { bg: 'rgba(100,116,139,0.10)', color: '#64748B' },
  planning: { bg: 'rgba(14,165,233,0.10)', color: '#0EA5E9' },
  active: { bg: 'rgba(16,185,129,0.10)', color: '#10B981' },
  completed: { bg: 'rgba(139,92,246,0.10)', color: '#8B5CF6' },
  paused: { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B' },
};

export default function Projects() {
  const { t, lang } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/projects'); setProjects(data); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.projects.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{t.projects.subtitle}</p>

      <div className="mt-14" data-testid="projects-list">
        {loading ? <p className="text-muted-foreground">{t.common.loading}</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p, i) => {
              const st = STATUS_STYLE[p.status] || STATUS_STYLE.planning;
              const done = (p.roadmap || []).filter(r => r.done).length;
              const total = (p.roadmap || []).length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <motion.div key={p.id}
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}>
                  <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}
                    className="block p-7 rounded-3xl border border-border bg-card hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5 transition-all duration-300">
                    <div className="flex items-center justify-between mb-5">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" style={{ color: st.color, backgroundColor: st.bg }}>
                        {t.projects.status[p.status] || p.status}
                      </span>
                      <span className="text-xs uppercase tracking-widest text-muted-foreground">{p.category}</span>
                    </div>
                    <h3 className="font-display text-xl font-medium leading-snug tracking-tight text-balance">
                      {lang === 'it' ? p.title_it : p.title_en}
                    </h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {lang === 'it' ? p.summary_it : p.summary_en}
                    </p>
                    <div className="mt-6 space-y-3">
                      <div>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                          <span>{t.projects.roadmap}</span>
                          <span>{done}/{total}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full bg-brand-deep dark:bg-brand-gold-dark" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Users size={12} /> {p.volunteers_needed}</span>
                        <span className="inline-flex items-center gap-1"><Target size={12} /> €{p.budget_eur?.toLocaleString?.() || 0}</span>
                        <span className="inline-flex items-center gap-1 text-foreground">
                          <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
